import {useRouter} from 'next/router';
import {ReactNode, useEffect} from 'react';
import {requestContext} from '../../../common/requestContext';
import useTypedDispatch from '../../../common/useTypedDispatch';
import {Loading} from '../../../design-system/atoms/Loading/Loading';
import {getSerializedPagewebError} from '../../../errors/getSerializedPagewebError.function';
import {PagewebError} from '../../../errors/PagewebError.class';
import {
  AnnouncementContainerFragment,
  AnnouncementTarget,
} from '../../../graphql/generated/types';
import {useHelpsQuery} from '../../../wrapper/appWrapper';
import {
  setHelpData,
  setHelpId,
  setHelpPage,
  setTotalCount,
} from '../state/helpSlice';
import {HelpParams, HelpQueryType} from '../type';
import {getHelpData} from '../util/getHelpData';
import {getHelpPage} from '../util/getHelpPage';
import {getHelpUrl} from '../util/getHelpUrl';

export function HelpFetcherHeadless({children}: {children: ReactNode}) {
  const router = useRouter();
  const dispatch = useTypedDispatch();
  const {isMobile, isIos} = requestContext.requestInfo.display;
  const {
    data,
    error,
    isFetching: fetching,
  } = useHelpsQuery({
    target: isMobile
      ? isIos
        ? AnnouncementTarget.Ios
        : AnnouncementTarget.Android
      : AnnouncementTarget.All,
  });

  useEffect(() => {
    const {help_uid, help_page}: HelpParams = router.query;
    const helpParams = router.query.pathParams;

    if (data?.helps) {
      // 도움말 없으면 dispatch 하지 않음
      if (data.helps.totalCount === 0) {
        return;
      }

      const helpData = getHelpData(data.helps as AnnouncementContainerFragment);
      dispatch(setHelpData(helpData));
      dispatch(setTotalCount(data.helps.totalCount));

      const helpId = helpParams?.[0] ?? help_uid ?? '';
      const foundHelpPage = Number(help_page) || getHelpPage(helpData, helpId);
      dispatch(setHelpPage(foundHelpPage));
      dispatch(setHelpId(helpParams?.[0] ?? helpId ?? ''));

      // 페이지와 ID가 둘 다 들어온 경우 ID 기준으로 redirect
      if (help_page && helpId) {
        dispatch(setHelpId(helpId));
        router.replace(
          getHelpUrl({queryType: HelpQueryType.Id, queryValue: helpId})
        );
      } else {
        // 페이지와 ID가 둘 다 없는 경우 첫 글로 redirect
        const hasNotBothPageAndId = !help_page && !helpId;
        // 1페이지인 경우 첫 글을 항상 열어주도록 구현. 첫 글로 redirect ( 기획 요청 사항 )
        const isFirstPage = help_page && Number(help_page) === 1;
        // query로 들어온 페이지가 공지사항 총 페이지 범위를 벗어난 경우 첫 글로 redirect
        const isExceedPageRange =
          foundHelpPage < 1 || foundHelpPage > data.helps.totalCount;
        if (hasNotBothPageAndId || isFirstPage || isExceedPageRange) {
          dispatch(setHelpId(helpData[0].id));
          router.replace(
            getHelpUrl({
              queryType: HelpQueryType.Id,
              queryValue: helpData[0].id,
            })
          );
        }
      }
    }
  }, [data, dispatch, router]);

  if (!children) {
    return null;
  }
  if (error) {
    throw new PagewebError(getSerializedPagewebError(error));
  }
  return (
    <>
      {fetching && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
      {!fetching && !!data && children}
    </>
  );
}
